import { checkPermissions } from "@elit-hair/elit-tools";
import { Alert, ButtonBase, Chip, IconButton } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { motion } from "motion/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowsMaximize, TbArrowsMinimize, TbSelector, TbSortAscending, TbSortDescending } from "react-icons/tb";

import StatusSummaries from "@/components/StatusSummaries";
import { Airports } from "@/constants";
import { airpots } from "@/constants/airpots";
import { passengerStatuses } from "@/constants/passengerStatuses";
import { PERMISSIONS } from "@/constants/permissions";
import { PASSENGER_STATUS } from "@/constants/status";
import { getAirportPermissions } from "@/helpers/auth";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import Panel from "@/layout/Panel";
import PanelTitle from "@/layout/PanelTitle";
import useAuth from "@/providers/hooks/useAuth";
import { useGetArrivalPassengers } from "@/requests/transport.queries";
import { IPlanPassenger } from "@/types/tasks.types";

import { LoadingCards } from "./components/LoadingCards";
import PassengerCard from "./components/PassengerCard";
import WelcomeTeamActions from "./components/WelcomeTeamActions";
import { WelcomeTeamTitle } from "./components/WelcomeTeamTitle";
import WelcomeTeamUpdateDialog from "./components/WelcomeTeamUpdateDialog";

export type ShowStatusConfirmation = [Exclude<PASSENGER_STATUS, "PICKED_UP" | "DONE">, PASSENGER_STATUS] | null;

export const calculateDiff = (startTime: DateTime): [number, number, number] | null => {
    const now = DateTime.now();
    if (!now || !startTime) {
        return null;
    }

    const diff = now.diff(startTime, ["hours", "minutes", "seconds"]);
    return [diff.hours || 0, diff.minutes || 0, diff.seconds || 0];
};

const getGroupMillis = (group: IPlanPassenger<"api">[], sortAsc: boolean) => {
    const times = group.map((p) => p.flightTime?.toMillis?.()).filter((n): n is number => typeof n === "number");
    if (times.length === 0) return sortAsc ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    return Math.min(...times);
};

const WelcomeTeamDashboard = () => {
    const { t } = useTranslation();

    const currentDate = DateTime.now();

    const [isFullScreen, setIsFullScreen] = useState(false);
    const handleFullScreen = (val?: boolean) => {
        setIsFullScreen(val ?? !isFullScreen);
    };

    const { userData } = useAuth();
    const hasEditPermission = checkPermissions(userData?.permissionCodes, [
        PERMISSIONS.TR_SVC_WELCOME_TEAM_PASSENGERS_SET_DATA,
    ]);
    const allowedAirports = useMemo(() => getAirportPermissions(userData?.permissionCodes || []), [userData]);

    const { data, isPending: loading } = useGetArrivalPassengers({
        date: currentDate,
        enabled: !!allowedAirports.length,
    });

    const [showStatusConfirmation, setShowStatusConfirmation] = useState<ShowStatusConfirmation>(null);

    const handleStatusConfirmation = (val?: ShowStatusConfirmation) => setShowStatusConfirmation(val ?? null);
    const [filters, setFilters] = useState<{ airport: string; status: null | string; search: string }>({
        airport: allowedAirports[0],
        status: null,
        search: "",
    });

    const handleFilters = (newFilters: Partial<{ search: string; airport: string; status: null | string }>) => {
        handleSelection([]);
        setFilters({ ...filters, ...newFilters });
    };

    const passengerList = useMemo(
        () => (data && data.filter((item) => item.origin.alias === filters.airport)) || [],
        [data, filters]
    );
    // Sort controls (default ascending by flight time)
    const [sortAsc, setSortAsc] = useState(true);
    const items = useMemo(() => {
        const list =
            passengerList.reduce<IPlanPassenger<"api">[][]>((prev, curr) => {
                if (filters.status && curr.status) {
                    const grouped = passengerStatuses.find((s) => s.status === filters.status);
                    const allowedStatuses = grouped?.statuses || [];
                    if (!allowedStatuses.includes(curr.status)) return prev;
                } else if (filters.search) {
                    const target = filters.search.toLowerCase().trim();

                    if (
                        !curr.name.toLowerCase().includes(target) &&
                        curr.companions.every((item) => !item.toLowerCase().includes(target)) &&
                        !curr.flightNumber.toLowerCase().includes(target)
                    )
                        return prev;
                }

                const passenger = prev.find((cells) => cells.some((cell) => cell.flightNumber === curr.flightNumber));
                if (passenger) passenger.push(curr);
                else prev.push([curr]);
                return prev;
            }, []) || [];

        // Sort groups by earliest flight time within each group
        list.sort((a, b) => {
            const timeA = getGroupMillis(a, sortAsc);
            const timeB = getGroupMillis(b, sortAsc);
            return sortAsc ? timeA - timeB : timeB - timeA;
        });

        return list;
    }, [passengerList, filters, sortAsc]);

    // Task search
    const [selectionList, setSelectionList] = useState<GridRowSelectionModel>([]);
    const handleSelection = (newList: GridRowSelectionModel) => {
        setSelectionList(newList);
    };

    // Airport selector

    const { size } = useWindowDimensions();

    const currentAirportImage = useMemo(() => airpots.find((a) => a.value === filters.airport)?.img, [filters]);

    return (
        <>
            <Panel title={<PanelTitle title={t("welcome_team")} />}>
                {allowedAirports.length ? (
                    <motion.div
                        initial={{ height: "100%", position: "static" }}
                        animate={{
                            height: isFullScreen ? "100vh" : "100%",
                            width: isFullScreen ? "100vw" : "100%",
                            position: isFullScreen ? "fixed" : "static",
                            padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                            top: 0,
                            left: 0,
                            background: isFullScreen ? "white" : "transparent", // Ensure it's visible in fullscreen
                            zIndex: isFullScreen ? 1000 : "auto",
                        }}
                        transition={{ duration: 0.3 }}
                        className="  overflow-auto relative !pb-16"
                    >
                        {loading ? (
                            <LoadingCards />
                        ) : (
                            <>
                                <div className=" sticky flex flex-col gap-1 top-0 bg-white z-50">
                                    <WelcomeTeamTitle
                                        search={filters.search}
                                        setSearch={(text) => handleFilters({ search: text })}
                                    />
                                    <div className="flex justify-between  items-center">
                                        <ButtonBase component="div">
                                            <Chip
                                                label={
                                                    <button
                                                        onClick={() => {
                                                            handleFilters({
                                                                airport:
                                                                    filters.airport === Airports.IST
                                                                        ? Airports.SAW
                                                                        : Airports.IST,
                                                            });
                                                        }}
                                                        disabled={allowedAirports.length === 1}
                                                        className="flex items-center gap-1"
                                                    >
                                                        <img src={currentAirportImage} className="w-5 h-5" alt="" />
                                                        {`${filters.airport} ${t("airport")}`} <TbSelector />
                                                    </button>
                                                }
                                            ></Chip>
                                        </ButtonBase>

                                        <div className="flex items-center   gap-2 py-1">
                                            <StatusSummaries<IPlanPassenger<"api">>
                                                tooltipPrefix="passengerStatuses."
                                                handleClick={(status: string | null) => handleFilters({ status })}
                                                activeStatus={filters.status}
                                                list={passengerList}
                                                statuses={passengerStatuses}
                                            />
                                            <IconButton
                                                aria-label="more"
                                                id="long-button"
                                                aria-haspopup="true"
                                                className=" !p-0"
                                                onClick={() => handleFullScreen()}
                                            >
                                                {isFullScreen ? <TbArrowsMinimize /> : <TbArrowsMaximize />}
                                            </IconButton>

                                            <IconButton
                                                aria-label="sort"
                                                id="sort-button"
                                                aria-haspopup="true"
                                                className=" !p-0"
                                                onClick={() => setSortAsc((prev) => !prev)}
                                            >
                                                {sortAsc ? <TbSortAscending /> : <TbSortDescending />}
                                            </IconButton>

                                            {hasEditPermission && (
                                                <WelcomeTeamActions
                                                    handleStatusConfirmation={handleStatusConfirmation}
                                                    items={data || []}
                                                    selectionList={selectionList}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 text-sm lg:text-base">
                                    {items.length === 0 ? (
                                        <Alert className=" mt-2" variant="outlined" severity="warning">
                                            {t(
                                                filters.status || filters.search ? "no_data_found" : "no_arrivals_today"
                                            )}
                                        </Alert>
                                    ) : (
                                        items.map((passengerGroup) => {
                                            return (
                                                <PassengerCard
                                                    passengerGroup={passengerGroup}
                                                    key={`task-card-${passengerGroup[0].id + Math.random().toString()}`}
                                                    hasEditPermission={hasEditPermission}
                                                    handleSelection={handleSelection}
                                                    selectionList={selectionList}
                                                    handleStatusConfirmation={handleStatusConfirmation}
                                                />
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        )}
                    </motion.div>
                ) : (
                    <Alert className=" mt-2" variant="outlined" severity="error">
                        {t("permission_not_found_welcome_page")}
                    </Alert>
                )}
            </Panel>

            <WelcomeTeamUpdateDialog
                showStatusConfirmation={showStatusConfirmation}
                items={data || []}
                selectionList={selectionList}
                onDetailRowSelectionModelChange={handleSelection}
                handleStatusConfirmation={handleStatusConfirmation}
            />
        </>
    );
};

export default WelcomeTeamDashboard;
